import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface Notification {
  id: string;
  title: string;
  body: string;
  category: string;
  created_at: string;
  is_read: boolean;
  is_published: boolean;
}

const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: notifs, error: notifsError } = await supabase
        .from('notifications')
        .select('id, title, body, category, created_at, is_published')
        .order('created_at', { ascending: false });

      if (notifsError || !notifs) {
        setNotifications([]);
        return;
      }

      const { data: reads } = await supabase
        .from('user_notification_reads')
        .select('notification_id')
        .eq('user_id', user.id);

      const readSet = new Set((reads || []).map((r: { notification_id: string }) => r.notification_id));

      setNotifications(
        notifs.map((n) => ({
          ...n,
          is_read: readSet.has(n.id),
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    await supabase
      .from('user_notification_reads')
      .upsert({ user_id: user.id, notification_id: notificationId })
      .eq('user_id', user.id);
  }, [user]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.is_read);
    if (!unread.length) return;
    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await supabase
      .from('user_notification_reads')
      .upsert(unread.map((n) => ({ user_id: user.id, notification_id: n.id })));
  }, [user, notifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return { notifications, loading, unreadCount, markAsRead, markAllAsRead, refetch: fetchNotifications };
};

export default useNotifications;
